import React, { useState } from 'react';
import { X, XCircle } from 'lucide-react';
import { useAdvisorStore } from '../../store/advisorStore';
import type { AdvisorManagement, AdvisorRejectionForm } from '../../types/advisor';

interface AdvisorRejectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  advisor: AdvisorManagement;
  onSuccess: () => void;
}

export const AdvisorRejectionModal: React.FC<AdvisorRejectionModalProps> = ({
  isOpen,
  onClose,
  advisor,
  onSuccess
}) => {
  const { rejectRequest, isLoading } = useAdvisorStore();
  const [formData, setFormData] = useState<AdvisorRejectionForm>({
    rejection_reason: '',
    notify_coordinators: true
  });
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.rejection_reason.trim()) {
      setError('Le motif de refus est requis');
      return;
    }

    if (formData.rejection_reason.trim().length < 10) {
      setError('Le motif doit contenir au moins 10 caractères');
      return;
    }

    try {
      await rejectRequest(advisor.id, formData);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Erreur lors du refus');
    }
  };

  const handleChange = (field: keyof AdvisorRejectionForm, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <XCircle className="w-6 h-6 text-red-600" />
            <h2 className="text-xl font-semibold text-gray-900">Refuser la demande</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Advisor Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-2">Candidat</h3>
            <p className="text-gray-700">{advisor.full_name}</p>
            <p className="text-sm text-gray-600">{advisor.email}</p>
          </div>

          {/* Rejection Reason */}
          <div>
            <label htmlFor="rejection_reason" className="block text-sm font-medium text-gray-700 mb-2">
              Motif du refus *
            </label>
            <textarea
              id="rejection_reason"
              value={formData.rejection_reason}
              onChange={(e) => handleChange('rejection_reason', e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Expliquez les raisons du refus (minimum 10 caractères)..."
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              {formData.rejection_reason.length} / 10 caractères minimum
            </p>
          </div>

          {/* Notification Option */}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="notify_coordinators"
              checked={formData.notify_coordinators}
              onChange={(e) => handleChange('notify_coordinators', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="notify_coordinators" className="text-sm text-gray-700">
              Notifier tous les coordinateurs de ce refus
            </label>
          </div>

          {/* Warning */}
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">
              <strong>Attention :</strong> Cette action est définitive. Le candidat recevra un email avec le motif du refus.
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isLoading || !formData.rejection_reason.trim() || formData.rejection_reason.trim().length < 10}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Refus...</span>
                </div>
              ) : (
                'Refuser la demande'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};